import React from "react";
import { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import secret from "../../../secret.json";

import { retrieveCommentaryPDFAPI } from "../../worksheets/worksheetAPI";
import {
  fetchLevels,
  fetchStudents,
  fetchAllUsers,
  setSelectedUser,
  fetchAssignments,
} from "../adminSlice";

import { refinePages } from "../../../utils/paginationUtils";
import {
  formatFullDateString,
  formatDateString,
  showStudentURL,
} from "../../../utils/textUtils";

import FilteringTab from "../../worksheets/components/common/FilteringTab/FilteringTab";
import WorksheetTypeBox from "../../worksheets/components/common/WorksheetTypeBox";
import WorksheetSearchBar from "../../worksheets/components/common/WorksheetSearchBar";
import LinkCopyButton from "../../../components/complex/LinkCopyButton";
import ReviewPageButton from "../../../components/complex/ReviewPageButton";
import CommentaryPDFButton from "../../../components/complex/CommentaryPDFButton";
import Pagination from "../../../components/Pagination";
import UserSelectBar from "./UserSelectBar";
import LevelTestMonitor from "./LevelTestMonitor";
import MonthlySummaryMonitor from "./MonthlySummaryMonitor";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

const AdminPage = () => {
  const [isMobileW, setIsMobileW] = React.useState(
    Math.min(window.innerWidth, window.screen.width) <= 768
  );
  React.useEffect(() => {
    const f = () =>
      setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const dispatch = useDispatch();

  const {
    results,
    current_page,
    total_pages,
    levels,
    allStudents,
    users,
    selectedUser,
  } = useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);

  const [keyword, setKeyword] = useState("");
  const [selectedParams, setSelectedParams] = useState({
    levelId: null,
    studentId: null,
    studentGrade: null,
  });

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handleUserSelect = (userId) => {
    const selectedUser = users.filter(
      (u) => u.id === parseInt(userId, 10)
    )[0] ?? {
      id: null,
      nickname: "",
    };
    dispatch(setSelectedUser(selectedUser));
    setSelectedParams({ levelId: null, studentId: null, studentGrade: null });
    setKeyword("");
  };

  const showCommentaryPDF = async (assignment) => {
    const pdfWindow = window.open();
    const response = await retrieveCommentaryPDFAPI(
      assignment.id,
      assignment.student.id
    );
    if (response.status !== 200) alert("해설지를 불러오는데 실패했습니다.");

    const file = new Blob([response.data], {
      type: "application/pdf",
    });
    const fileURL = URL.createObjectURL(file);
    pdfWindow.location.href = fileURL;
  };

  const showReviewPage = (assignment) => {
    if (!assignment.finished_at) return;
    const reviewURL = `${secret.STUDENT_BASE_URL}/review?id=${assignment.id}&session=${user.session}`;
    window.open(reviewURL, "_blank");
  };

  const handlePageClick = (page) => {
    dispatch(
      fetchAssignments({
        page,
        keyword: keyword,
        levelId: selectedParams.levelId,
        studentId: selectedParams.studentId,
        studentGrade: selectedParams.studentGrade,
      })
    );
  };

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUser.id) {
      dispatch(
        fetchAssignments({
          page: 1,
          keyword: keyword,
          levelId: selectedParams.levelId,
          studentId: selectedParams.studentId,
          studentGrade: selectedParams.studentGrade,
        })
      );
      dispatch(fetchLevels());
      dispatch(fetchStudents());
    }
  }, [dispatch, selectedUser, keyword, selectedParams]);

  return (
    <div
      style={{
        padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "10px 14px 32px" : "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">학원별 출제 내역</h3>
        )}
        <UserSelectBar
          users={users}
          selectedUser={selectedUser}
          handleUserSelect={handleUserSelect}
        />
        <div style={{ flex: isMobileW ? 1 : "none", width: isMobileW ? "auto" : "30%", minWidth: isMobileW ? 0 : "220px" }}>
          <WorksheetSearchBar handleFilter={setKeyword} />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: isMobileW ? "column" : "row",
          alignItems: "stretch",
          marginTop: "10px",
        }}
      >
        <FilteringTab
          levels={levels}
          allStudentList={allStudents}
          selectedParams={selectedParams}
          setSelectedParams={setSelectedParams}
        />
        <div
          style={{
            width: isMobileW ? "100%" : "85%",
            minWidth: 0,
            backgroundColor: "#FFF",
            paddingBottom: "5px",
            borderRadius: "18px",
            border: "1px solid #E6EDEC",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table className="data-table" style={{ minWidth: "860px" }}>
            <colgroup>
              <col style={{ width: "90px" }} />
              <col />
              <col style={{ width: "80px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "66px" }} />
              <col style={{ width: "160px" }} />
            </colgroup>
            <tbody>
              <tr className="data-table__header-row">
                <td className="cell-nowrap">이름</td>
                <td className="cell-nowrap" style={{ paddingLeft: "15px" }}>
                  학습지명
                </td>
                <td className="cell-center cell-nowrap">마감일</td>
                <td className="cell-center cell-nowrap">채점</td>
                <td className="cell-center cell-nowrap">해설지</td>
                <td className="cell-center cell-nowrap">제출 시간</td>
              </tr>
              {results &&
                results.map((item, index) => {
                  const student = allStudents.find(
                    (student) => student.id === item.student.id
                  );
                  return (
                    <tr key={item.id}>
                      <td className="cell-nowrap">
                        {student?.name ? (
                          <a
                            href={showStudentURL(item.student.session)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="학생 페이지 열기"
                            className="cell-link"
                          >
                            {student.name}
                          </a>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", minWidth: 0 }}>
                          <span style={{ flexShrink: 0 }}>
                            <LinkCopyButton
                              URL={showStudentURL(item.student.session)}
                            />
                          </span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{item.title}</span>
                          <span style={{ marginLeft: "2px", flexShrink: 0 }}>
                            <WorksheetTypeBox type={item.type} />
                          </span>
                        </div>
                      </td>
                      <td className="cell-center cell-nowrap">
                        {formatDateString(item.closed_at)}
                      </td>
                      <td className="cell-center">
                        <ReviewPageButton
                          assignment={item}
                          showReviewPage={showReviewPage}
                        />
                      </td>
                      <td className="cell-center" style={{ position: "relative" }}>
                        {item.commentary_pdf_path !== "" && (
                          <CommentaryPDFButton
                            assignment={item}
                            showCommentaryPDF={showCommentaryPDF}
                          />
                        )}
                      </td>
                      <td className="cell-center cell-nowrap">
                        {formatFullDateString({
                          dateString: item.finished_at,
                          defaultString: "미제출",
                        })}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          <Pagination
            visiblePages={visiblePages}
            current_page={current_page}
            handlePageClick={handlePageClick}
          />
        </div>
      </div>
      {/* 레벨테스트 모니터링. 응시 현황과 응시코드를 훑어보는 용도다. */}
      <MonthlySummaryMonitor />
      <LevelTestMonitor />

    </div>
  );
};

export default AdminPage;
